import React, {FC} from 'react';
import Select, {StylesConfig} from 'react-select';
import {Controller, useFormContext} from 'react-hook-form';
import {FormError, FormHint, FormLabel, FormRow} from './Layout';

interface ISelectInputProps {
  options: {value: string; label: string}[];
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  hint?: React.ReactNode;
}

// Mirrors FIELD_CLASSES on TextInput so a select and a text box in the same form read as one family.
const SELECT_STYLES: StylesConfig<{value: string; label: string}, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    fontSize: 13,
    color: '#33364b',
    borderRadius: 6,
    boxShadow: 'none',
    backgroundColor: state.isDisabled ? '#f7f7f9' : '#fff',
    borderColor: state.isFocused ? '#654dcb' : '#dcdee5',
    '&:hover': {borderColor: state.isFocused ? '#654dcb' : '#dcdee5'},
  }),
  valueContainer: (base) => ({...base, padding: '0 12px'}),
  input: (base) => ({...base, margin: 0, padding: 0, color: '#33364b'}),
  singleValue: (base) => ({...base, color: '#33364b'}),
  placeholder: (base) => ({...base, color: '#9ca3af'}),
  indicatorSeparator: () => ({display: 'none'}),
  // The menu portals to body so a dialog's scroll container cannot clip it; the z-index clears the dialog backdrop.
  // Radix modals set pointer-events: none on body while open, which the portaled menu would inherit, leaving its
  // options visible but unclickable (keyboard selection still works since focus stays in the dialog).
  menuPortal: (base) => ({...base, zIndex: 20000, pointerEvents: 'auto'}),
  dropdownIndicator: (base) => ({...base, padding: 6, color: '#9ca3af'}),
  clearIndicator: (base) => ({...base, padding: 6, color: '#9ca3af'}),
  menu: (base) => ({
    ...base,
    marginTop: 4,
    borderRadius: 6,
    border: '1px solid #dcdee5',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: '8px 12px',
    color: state.isSelected ? '#654dcb' : '#33364b',
    backgroundColor: state.isSelected ? '#efedfb' : state.isFocused ? '#f8f7fe' : '#fff',
    '&:active': {backgroundColor: '#efedfb'},
  }),
};

export const SelectInput: FC<ISelectInputProps> = ({options, label, name, placeholder, disabled, required, hint}) => {
  const form = useFormContext();

  const error = form.formState.errors[name];

  return (
    <FormRow>
      <FormLabel htmlFor={`field-${name}`}>
        {label}
        {required && <span className="ml-0.5 text-[#c2410c]">*</span>}
      </FormLabel>

      <Controller
        name={name}
        control={form.control}
        render={({field}) => {
          return (
            <Select
              {...field}
              inputId={`field-${name}`}
              placeholder={placeholder}
              isDisabled={disabled}
              isClearable
              isSearchable
              options={options}
              styles={SELECT_STYLES}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              value={options.find((c) => c.value === field.value)}
              onChange={(val) => field.onChange(val?.value || '')}
            />
          );
        }}
      />

      {hint && !error && <FormHint>{hint}</FormHint>}
      {error && <FormError role="alert">{String(error.message)}</FormError>}
    </FormRow>
  );
};
